import * as React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { getViewMode } from '@/lib/utils'
import { Calendar, Loader2, Grid3x3, List } from 'lucide-react'
import { motion } from 'framer-motion'

export function TodayPage() {
  const [viewMode, setViewMode] = React.useState(getViewMode())
  const { ref, inView } = useInView()

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['todayNews'],
    queryFn: ({ pageParam = 1 }) => newsApi.getTodayNews(pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage?.data?.next) return undefined
      const url = new URL(lastPage.data.next)
      return url.searchParams.get('page')
    },
  })

  // Load more when scrolling
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Save view mode
  const handleViewModeChange = (mode) => {
    setViewMode(mode)
    localStorage.setItem('viewMode', mode)
  }

  const articles = data?.pages.flatMap(page => page.data.results || []) || []
  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-8 h-8 text-primary" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Today's News
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                {today}
              </p>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1">
              <button
                onClick={() => handleViewModeChange('card')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'card'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title="Card View"
              >
                <Grid3x3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleViewModeChange('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title="List View"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Stats */}
          {!isLoading && articles.length > 0 && (
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {articles.length} articles published today
              </span>
            </div>
          )}
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <NewsCardSkeleton key={i} variant={viewMode} />
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="text-center py-12">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Failed to Load Articles
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Something went wrong. Please try again.
            </p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        )}

        {/* No Articles */}
        {!isLoading && !isError && articles.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <Calendar className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Articles Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Check back later for today's news updates.
            </p>
          </div>
        )}

        {/* Articles Grid/List */}
        {!isLoading && !isError && articles.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className={
              viewMode === 'card'
                ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {articles.map((article) => (
              <NewsCard
                key={article.alias}
                article={article}
                variant={viewMode}
              />
            ))}
          </motion.div>
        )}

        {/* Load More Trigger */}
        {!isLoading && !isError && articles.length > 0 && (
          <div ref={ref} className="py-8">
            {isFetchingNextPage && (
              <div className="flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
          </div>
        )}

        {/* End of Results */}
        {!hasNextPage && articles.length > 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            You've reached the end of today's news
          </div>
        )}
      </div>
    </div>
  )
}
